import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData, fnGetDate } from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global, ExecuteRunner } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import SharedForm from "@widgets/SharedForm";
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";

interface Props {
  Menuprops: menuInfo;
}

const SN_PRD_CAS_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);
  const SharedFormHref = useRef<any>(null);
  const [focusedRowDataH, setFocusedRowDataH] = useState<any>();
  const { userInfo } = global(); // Login user 정보
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [sharedFormH, setSharedFormH] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));

        setSharedFormH(rtn.form.freeform1);

        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onExecute,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_prd_cas_reg_002_01",
        params: searchDataToUse,
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onExecute
  const onExecute = async (id: string) => {
    try {
      const checkedRows = getGridCurrent("grid1").getCheckedRows();
      if (checkedRows.length < 1) {
        await showDialog("alter", "선택된 항목이 없습니다.");
        return;
      }

      let hasInvalid = false;
      let invalidData: any;
      checkedRows.forEach((item) => {
        if (item.qt !== item.qt_comp_good + item.qt_comp_bad) {
          hasInvalid = true;
          invalidData = item;
        }
      });

      if (hasInvalid) {
        await showDialog(
          "alter",
          `입고 수량과 양불수량 합계가 일치하지 않는 건이 있습니다.\n의뢰번호:${invalidData.no_repair} 의뢰항번:${invalidData.no_line}`
        );
        return false;
      }

      switch (id) {
        case "complete":
          // 26.09.21 kdy 수리완료 시각은 서버 시간으로 기록(PC 시간 오차 방지)
          const tmRepair = await fnGetDate();
          checkedRows.forEach((item) => {
            // WST(폐기)인 경우
            if (item.cd_repair === "WST") {
              item.st_repair = "X";
            } else {
              item.st_repair = "C";
            }
            item.cd_emp_repair = userInfo?.cd_emp;
            item.cd_dept_repair = userInfo?.cd_dept;
            item.tm_repair = tmRepair;
          });
          break;
        case "cancel":
          checkedRows.forEach((item) => {
            if (item.cd_repair !== "F") {
              hasInvalid = true;
              invalidData = item;
            }
            item.st_repair = "F";
          });

          if (hasInvalid) {
            await showDialog(
              "alter",
              `진행상태가 입고가 아닌 건이 있습니다.\n의뢰번호:${invalidData.no_repair} 의뢰항번:${invalidData.no_line}`
            );
            return false;
          }
          break;
      }

      const repairlChanges = {
        insert: [] as any[],
        update: [] as any[],
        delete: [] as any[],
        modifiedRowcount: checkedRows.length,
      };

      checkedRows.forEach((item) => {
        repairlChanges.update.push(item);
      });
      const repairl_reChanges = getGridCurrent("grid2").getChange();

      // 요청 데이터 구성
      const SaveData = {
        repairl: repairlChanges,
        repairl_re: repairl_reChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_PRD_CAS_REG_002_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      onSearch(null);
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region GridH Events
  const onFocusedRowChangedH = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      setFocusedRowDataH(selectedRow);
      getGridCurrent("grid2").applyFilters({
        no_repair: selectedRow.no_repair,
        no_line_repair: selectedRow.no_line,
      });
    } else {
      setFocusedRowDataH(null);
      getGridCurrent("grid2").applyFilters({
        no_repair: "",
        no_line_repair: -1,
      });
    }
  };
  //#endregion

  //#region SharedForm Events
  const onButtonClick = async (id: string) => {
    const focusedRowNodeL = getGridCurrent("grid1").focusedRowNode();
    if (!focusedRowNodeL) return;

    if (focusedRowNodeL.data.st_repair !== "F") {
      await showDialog("alter", "진행상태가 입고인 건만 입력할 수 있습니다.");
    }

    const qt = focusedRowNodeL.data.qt;

    switch (id) {
      case "btn_good": // 양품적용
        focusedRowNodeL.setDataValue("qt_comp_good", qt, "noEvent");
        focusedRowNodeL.setDataValue("qt_comp_bad", 0, "noEvent");
        break;
      case "btn_bad": // 불량적용
        focusedRowNodeL.setDataValue("qt_comp_good", 0, "noEvent");
        focusedRowNodeL.setDataValue("qt_comp_bad", qt, "noEvent");
        break;
    }
    setFocusedRowDataH({
      ...focusedRowNodeL.data,
    });
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged: onFocusedRowChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="40%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{
              rowSelection: "single",
              isEditable: false,
              ischeckboxSelection: true,
              title: "입고정보",
            }}
            events={grids.grid1.events}
            header={
              <div>
                <SnButton
                  text={"수리완료"}
                  onClick={() => {
                    ExecuteRunner.run("complete");
                  }}
                />
                <SnButton
                  text={"수리취소"}
                  onClick={() => {
                    ExecuteRunner.run("cancel");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="50%">
          <SharedForm
            ref={SharedFormHref}
            data={focusedRowDataH}
            controls={sharedFormH}
            gridref={gridRefs.current["grid1"]}
            // onValueChaged={onValueChagedSharedFormH}
            onButtonClick={onButtonClick}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel3" orgSize="40%">
          <SnGrid
            ref={gridRefs.current["grid2"]} // Grid Name
            columnConfig={grids.grid2.columns} // 컬럼 설정 전달
            rowData={grids.grid2.dataSource} // Datasource
            etcProps={{ rowSelection: "multiple", title: "불량유형" }}
          />
        </SnResizeBar>
      </div>
    </div>
  );
  //#endregion
});

export default SN_PRD_CAS_REG_002;
